import { useCallback, useMemo, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

interface TabItem {
  value: string;
  label: string;
  content: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
}

interface TabsWithPersistenceProps {
  tabs: TabItem[];
  paramName?: string;
  defaultTab?: string;
  persistKey?: string;
  onTabChange?: (value: string) => void;
  className?: string;
  tabsListClassName?: string;
}

const getLocalStorageKey = (persistKey: string) => `admin-tab-${persistKey}`;

const readPersistedTab = (persistKey: string | undefined): string | null => {
  if (!persistKey) {return null;}
  try {
    return localStorage.getItem(getLocalStorageKey(persistKey));
  } catch {
    return null;
  }
};

const writePersistedTab = (persistKey: string | undefined, value: string) => {
  if (!persistKey) {return;}
  try {
    localStorage.setItem(getLocalStorageKey(persistKey), value);
  } catch {
    // localStorage может быть недоступен
  }
};

export const TabsWithPersistence = ({
  tabs,
  paramName = 'tab',
  defaultTab,
  persistKey,
  onTabChange,
  className,
  tabsListClassName,
}: TabsWithPersistenceProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const validValues = useMemo(() => new Set(tabs.map((t) => t.value)), [tabs]);

  const fallbackTab = defaultTab ?? tabs[0]?.value ?? '';

  const activeTab = useMemo(() => {
    const urlValue = searchParams.get(paramName);
    if (urlValue && validValues.has(urlValue)) {
      return urlValue;
    }

    const persistedValue = readPersistedTab(persistKey);
    if (persistedValue && validValues.has(persistedValue)) {
      return persistedValue;
    }

    return fallbackTab;
  }, [searchParams, paramName, validValues, persistKey, fallbackTab]);

  const handleTabChange = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(paramName, value);
          return next;
        },
        { replace: true },
      );

      writePersistedTab(persistKey, value);
      onTabChange?.(value);
    },
    [setSearchParams, paramName, persistKey, onTabChange],
  );

  if (tabs.length === 0) {return null;}

  return (
    <Tabs
      value={activeTab}
      onValueChange={handleTabChange}
      className={cn(className)}
    >
      <TabsList className={cn('mb-6', tabsListClassName)}>
        {tabs.map((tab) => (
          <TabsTrigger
            key={tab.value}
            value={tab.value}
            disabled={tab.disabled}
            className="gap-1.5"
          >
            {tab.icon}
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>
      {tabs.map((tab) => (
        <TabsContent
          key={tab.value}
          value={tab.value}
          forceMount
          className="mt-0 data-[state=inactive]:hidden"
        >
          {tab.content}
        </TabsContent>
      ))}
    </Tabs>
  );
};
